import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { invoiceService } from '../services/api';

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const data = await invoiceService.getAll();
      setInvoices(data);
    } catch (err) {
      console.error('Error loading invoices:', err);
      setError('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'badge-gray',
      sent: 'badge-info',
      paid: 'badge-success',
      overdue: 'badge-danger',
      cancelled: 'badge-gray',
    };
    return badges[status] || 'badge-gray';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (value) => {
    if (!value) return '$0.00';
    return '$' + Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const filteredInvoices = statusFilter === 'all' 
    ? invoices 
    : invoices.filter((inv) => inv.status === statusFilter);

  // Calculate totals
  const totals = {
    draft: invoices.filter((i) => i.status === 'draft').reduce((s, i) => s + (i.amount || 0), 0),
    sent: invoices.filter((i) => i.status === 'sent').reduce((s, i) => s + (i.amount || 0), 0),
    overdue: invoices.filter((i) => i.status === 'overdue').reduce((s, i) => s + (i.amount || 0), 0),
    paid: invoices.filter((i) => i.status === 'paid').reduce((s, i) => s + (i.amount || 0), 0),
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Invoices | CR Hood Admin</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
            <p className="text-gray-500 mt-1">Manage client invoices and track payments</p>
          </div>
          <Link to="/invoices/new" className="btn btn-primary">
            + New Invoice
          </Link>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card bg-gray-50">
            <div className="text-sm text-gray-500">Draft</div>
            <div className="text-2xl font-bold text-gray-700">{formatCurrency(totals.draft)}</div>
            <div className="text-xs text-gray-400">{invoices.filter((i) => i.status === 'draft').length} invoices</div>
          </div>
          <div className="card bg-blue-50">
            <div className="text-sm text-blue-600">Outstanding</div>
            <div className="text-2xl font-bold text-blue-700">{formatCurrency(totals.sent)}</div>
            <div className="text-xs text-blue-400">{invoices.filter((i) => i.status === 'sent').length} invoices</div>
          </div>
          <div className="card bg-red-50">
            <div className="text-sm text-red-600">Overdue</div>
            <div className="text-2xl font-bold text-red-700">{formatCurrency(totals.overdue)}</div>
            <div className="text-xs text-red-400">{invoices.filter((i) => i.status === 'overdue').length} invoices</div>
          </div>
          <div className="card bg-green-50">
            <div className="text-sm text-green-600">Paid (All Time)</div>
            <div className="text-2xl font-bold text-green-700">{formatCurrency(totals.paid)}</div>
            <div className="text-xs text-green-400">{invoices.filter((i) => i.status === 'paid').length} invoices</div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">Filter:</span>
          {['all', 'draft', 'sent', 'paid', 'overdue', 'cancelled'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                statusFilter === status
                  ? 'bg-accent text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {/* Invoices Table */}
        <div className="card overflow-hidden">
          {filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No invoices</h3>
              <p className="mt-1 text-sm text-gray-500">
                {statusFilter === 'all' 
                  ? 'Get started by creating a new invoice.'
                  : `No ${statusFilter} invoices found.`}
              </p>
              {statusFilter === 'all' && (
                <div className="mt-6">
                  <Link to="/invoices/new" className="btn btn-primary">
                    + New Invoice
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Issue Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link 
                        to={`/invoices/${invoice.id}`}
                        className="font-medium text-gray-900 hover:text-accent"
                      >
                        {invoice.invoiceNumber}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {invoice.client ? (
                        <Link 
                          to={`/clients/${invoice.client.id}`}
                          className="text-gray-600 hover:text-accent"
                        >
                          {invoice.client.company || invoice.client.name}
                        </Link>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-medium">
                      {formatCurrency(invoice.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(invoice.issueDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(invoice.dueDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`badge ${getStatusBadge(invoice.status)}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <Link 
                        to={`/invoices/${invoice.id}`}
                        className="text-accent hover:underline"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
